/**
 * Shop-level worked example.
 *
 * FIXTURE — the same caveat as `job.ts`. Screens take their data as props, so
 * every one of these becomes a query without the screen changing.
 */

import type { CatalogItem } from '../data/repo.js';

export interface JobRow {
  reference: string;
  customer: string;
  customerEmail: string | null;
  needsApproval: boolean;
  vehicle: string;
  vin: string | null;
  impact: string;
  status: 'open' | 'issued' | 'awaiting_approval' | 'invoiced' | 'printed'
        | 'total_loss' | 'declined';
  total: number | null;
  shotsTaken: number;
  proposalsTotal: number;
  proposalsDecided: number;
}

export const JOBS: JobRow[] = [
  { reference: 'EST-2482', customer: 'Northgate Plumbing', customerEmail: 'ap@northgateplumbing.ca',
    needsApproval: true, vehicle: '2019 F-150 XLT SuperCrew', vin: '1FTEW1EP5KFA12345',
    impact: 'Front left', status: 'open', total: null,
    shotsTaken: 1, proposalsTotal: 0, proposalsDecided: 0 },
  { reference: 'EST-2481', customer: 'D. Whitfield', customerEmail: 'dwhitfield@gmail.com',
    needsApproval: false, vehicle: '2019 F-150 XLT', vin: '1FTEW1EP1KFB67890',
    impact: 'Front left', status: 'printed', total: 2412.60,
    shotsTaken: 3, proposalsTotal: 5, proposalsDecided: 5 },
  { reference: 'EST-2479', customer: 'Northgate Plumbing', customerEmail: 'ap@northgateplumbing.ca',
    needsApproval: true, vehicle: '2018 Transit 250', vin: '1FTYR1ZM5JKA54321',
    impact: 'Left side', status: 'awaiting_approval', total: 1876.20,
    shotsTaken: 3, proposalsTotal: 4, proposalsDecided: 4 },
  { reference: 'EST-2478', customer: 'S. Baptiste', customerEmail: null,
    needsApproval: false, vehicle: '2016 Civic LX', vin: '2HGFC2F59GH098765',
    impact: 'Front', status: 'total_loss', total: 8942.10,
    shotsTaken: 3, proposalsTotal: 6, proposalsDecided: 6 },
  { reference: 'EST-2474', customer: 'Prairie Auto Recon', customerEmail: 'estimates@prairieautorecon.ca',
    needsApproval: true, vehicle: '2021 RAV4 Hybrid', vin: 'JTMRWRFV5MD112233',
    impact: 'Rear', status: 'invoiced', total: 3104.65,
    shotsTaken: 3, proposalsTotal: 3, proposalsDecided: 3 },
];

export interface CustomerRow {
  name: string;
  kind: 'individual' | 'fleet' | 'dealer';
  needsApproval: boolean;
  terms: string;
  vehicles: number;
  since: string;
}

export const CUSTOMERS: CustomerRow[] = [
  { name: 'Northgate Plumbing', kind: 'fleet', needsApproval: true, terms: 'NET 30',
    vehicles: 14, since: 'Mar 2024' },
  { name: 'Prairie Auto Recon', kind: 'dealer', needsApproval: true, terms: 'NET 15',
    vehicles: 31, since: 'Nov 2025' },
  { name: 'D. Whitfield', kind: 'individual', needsApproval: false, terms: 'Pays at counter',
    vehicles: 1, since: 'Aug 2026' },
  { name: 'S. Baptiste', kind: 'individual', needsApproval: false, terms: 'Pays at counter',
    vehicles: 2, since: 'Jun 2026' },
];

/**
 * The shop's ONE catalog.
 *
 * There used to be a second, shorter list for the worked job. Two catalogs for
 * one shop is how a demo ends up quoting a part at one price on the estimate
 * and another in the parts screen — so the job now prices against this list.
 * Descriptions match what the AI proposes, because a catalog lookup that misses
 * on wording is the prompt firing for a part the shop already owns.
 */
export const CATALOG_ROWS: CatalogItem[] = [
  { partNumber: 'FL3Z-17D957-AA', description: 'Bumper cover, front',
    partType: 'new_oem', price: 410.00, source: 'bulk' },
  { partNumber: 'JL3Z-13008-K', description: 'Headlamp assembly, LH',
    partType: 'new_oem', price: 520.00, source: 'bulk' },
  { partNumber: 'FL3Z-8005-C', description: 'Radiator support',
    partType: 'new_oem', price: 386.50, source: 'bulk' },
  { partNumber: 'FL3Z-16005-A', description: 'Fender, LH',
    partType: 'new_oem', price: 298.00, source: 'bulk' },
  { partNumber: 'MISC-CLIP-07', description: 'Bumper clip set',
    partType: 'aftermarket', price: 12.40, source: 'asked_once' },
  { partNumber: 'HAZ-WASTE', description: 'Hazardous waste disposal',
    partType: 'sublet', price: 5.00, source: 'asked_once' },
];

export interface TenantRow {
  id: string; name: string; country: string; users: number; jobs: number; live: boolean;
}

export const TENANTS: TenantRow[] = [
  { id: 'T-001', name: 'Riverbend Collision', country: 'CA', users: 3, jobs: 412, live: true },
  { id: 'T-002', name: 'Al Reem Auto Body', country: 'AE', users: 2, jobs: 58, live: true },
  { id: 'T-003', name: 'Vestland Karosseri', country: 'NO', users: 1, jobs: 9, live: false },
];

export interface UserRow {
  email: string; role: string; language: string; status: 'Active' | 'Invited';
}

export const USERS: UserRow[] = [
  { email: 'pakwaten@gmail.com', role: 'Owner / admin', language: 'English', status: 'Invited' },
  { email: 'sofia@riverbendcollision.ca', role: 'Estimator', language: 'Français', status: 'Active' },
  { email: 'dan@riverbendcollision.ca', role: 'Technician', language: 'English', status: 'Active' },
];

/** Twelve weeks. The two series that decide whether the product is working. */
export const WEEKS = ['W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12'];
export const ACCEPTANCE = [41, 46, 44, 52, 55, 53, 58, 61, 60, 64, 66, 68];
export const CATALOG_PROMPTS = [38, 31, 27, 24, 19, 16, 14, 11, 9, 8, 7, 6];

export const STATUS_LABEL: Record<JobRow['status'], string> = {
  open: 'Open',
  issued: 'Issued',
  awaiting_approval: 'Awaiting approval',
  invoiced: 'Invoiced',
  printed: 'Printed',
  total_loss: 'Total loss',
  declined: 'Declined',
};

/** Every status, in lifecycle order, derived from the labels so a new status
 *  cannot be added to one list and forgotten in the other. The dashboard used
 *  to hardcode its own five and silently dropped `issued` and `declined` —
 *  jobs that exist, carry money, and appeared in no bucket at all. */
export const JOB_STATUSES = Object.keys(STATUS_LABEL) as Array<JobRow['status']>;
